/**
 * The REPORT-UX1 workspace additions to the report detail page:
 * Tätigkeitsbereich card, Notizen card, Anhänge card and the
 * cancellation banner.
 *
 * All four speak the page's existing card language (``DetailCard`` /
 * the amber accent) and are presentational shells around SHARED
 * engines:
 *
 * - the category CONFIG comes from the organisation's own catalog — the
 *   one table of 13 codes with their German labels, so the task page
 *   and the report page cannot disagree what ``handover`` is called;
 * - the notes and attachments UIs are the generalized engines from the
 *   task workspace, rebound onto the work-report API — one behaviour,
 *   two parents, no copy-paste drift.
 */
import React, { useMemo } from "react";
import { Ban } from "lucide-react";

import {
  createAdminWorkReportComment,
  deleteAdminWorkReportAttachment,
  deleteAdminWorkReportComment,
  fetchAdminWorkReportAttachmentDownloadUrl,
  fetchAdminWorkReportAttachments,
  fetchAdminWorkReportComments,
  updateAdminWorkReportComment,
  uploadAdminWorkReportAttachment,
} from "../../../api/adminData";
import { formatDateTime } from "../../../utils/datetime";
import { useCategoryDisplay } from "../activityCategories/ActivityCategoryChip";
import TaskAttachmentsSection from "../tasks/TaskAttachmentsSection";
import TaskCommentsSection from "../tasks/TaskCommentsSection";
import { DetailCard } from "./WorkReportDetailCards";

/** The hint both frozen cards show — one sentence, one truth. */
export const CANCELLED_READONLY_HINT =
  "Der Rapport ist storniert und schreibgeschützt.";

// ──────────────────────────────────────────────────────────────────────
// Tätigkeitsbereich
// ──────────────────────────────────────────────────────────────────────

/**
 * Sidebar card for the report's area of work. Same content rules as the
 * task page's TaskCategoryCard (label + description, "Nicht zugewiesen"
 * for NULL, never a raw code), rendered in THIS page's card chrome.
 */
export function WorkReportCategoryCard({ code }) {
  // RESP2d: resolved through the organisation's catalog — see
  // TaskCategoryCard, which this deliberately mirrors.
  const { state, label, description, icon: Icon, colorToken, isInactive } =
    useCategoryDisplay(code);
  const known = state === "known";
  return (
    <DetailCard
      headingId="report-category-heading"
      title="Tätigkeitsbereich"
    >
      {known ? (
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border ${colorToken}`}
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="m-0 text-[13px] font-semibold text-foreground dark:text-[#c5cbe0]">
              {label}
            </p>
            {description ? (
              <p className="m-0 mt-1 text-[11px] leading-relaxed text-muted-foreground dark:text-[#4a5070]">
                {description}
              </p>
            ) : null}
            {isInactive ? (
              <p className="m-0 mt-1 text-[11px] font-medium text-muted-foreground dark:text-[#4a5070]">
                Dieser Bereich ist deaktiviert.
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <p className="m-0 text-[13px] font-medium text-muted-foreground dark:text-[#4a5070]">
            {label}
          </p>
          <p className="m-0 mt-1 text-[11px] leading-relaxed text-muted-foreground dark:text-[#4a5070]">
            Über „Rapport bearbeiten" lässt sich ein Bereich nachtragen.
          </p>
        </>
      )}
    </DetailCard>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Notizen
// ──────────────────────────────────────────────────────────────────────

/**
 * Internal running notes on ONE report — distinct from its neighbours
 * by design: the description says what work was done, the activity feed
 * records what the SYSTEM did, the attachments hold files, and this is
 * where people write to each other about the report afterwards.
 */
export function WorkReportCommentsSection({
  reportId,
  currentUserId,
  onChanged,
  readOnly = false,
}) {
  const api = useMemo(
    () => ({
      list: () => fetchAdminWorkReportComments(reportId),
      create: (body) => createAdminWorkReportComment(reportId, body),
      update: (commentId, body) =>
        updateAdminWorkReportComment(reportId, commentId, body),
      remove: (commentId) =>
        deleteAdminWorkReportComment(reportId, commentId),
    }),
    [reportId]
  );
  return (
    <DetailCard headingId="report-notes-heading" title="Notizen">
      <TaskCommentsSection
        taskId={reportId}
        currentUserId={currentUserId}
        onCommentChanged={onChanged}
        api={api}
        readOnly={readOnly}
        readOnlyHint={readOnly ? CANCELLED_READONLY_HINT : null}
        textareaId="report-comment-body"
      />
    </DetailCard>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Anhänge
// ──────────────────────────────────────────────────────────────────────

/**
 * Files on one report. On a cancelled report the card turns read-only:
 * list and download stay (the files are part of the preserved history),
 * upload and delete disappear — mirroring the backend's 409 freeze.
 */
export function WorkReportAttachmentsSection({
  reportId,
  onChanged,
  readOnly = false,
}) {
  const api = useMemo(
    () => ({
      list: () => fetchAdminWorkReportAttachments(reportId),
      upload: (file) => uploadAdminWorkReportAttachment(reportId, file),
      downloadUrl: (attachmentId) =>
        fetchAdminWorkReportAttachmentDownloadUrl(reportId, attachmentId),
      remove: (attachmentId) =>
        deleteAdminWorkReportAttachment(reportId, attachmentId),
    }),
    [reportId]
  );
  return (
    <TaskAttachmentsSection
      taskId={reportId}
      onChanged={onChanged}
      api={api}
      readOnly={readOnly}
      readOnlyHint={readOnly ? CANCELLED_READONLY_HINT : null}
      headingId="report-attachments-heading"
    />
  );
}

// ──────────────────────────────────────────────────────────────────────
// Storno-Banner
// ──────────────────────────────────────────────────────────────────────

/**
 * The one prominent statement that this report is cancelled: reason,
 * who, when. Text carries the meaning — the colour is reinforcement,
 * never the only signal.
 */
export function WorkReportCancelledBanner({ report }) {
  if (!report?.is_cancelled) return null;
  const who =
    report.cancelled_by_full_name ||
    report.cancelled_by_email ||
    "Unbekannt";
  return (
    <div
      role="status"
      className="mb-4 rounded-[12px] border border-destructive/40 bg-destructive/10 p-4 dark:border-[#3a1f24] dark:bg-[#1e0d10]"
    >
      <p className="m-0 flex items-center gap-2 text-[13px] font-semibold text-destructive dark:text-[#ff6b6b]">
        <Ban className="h-4 w-4 shrink-0" aria-hidden="true" />
        Dieser Rapport wurde storniert.
      </p>
      <p className="m-0 mt-1 text-[12px] text-foreground dark:text-[#c5cbe0]">
        {report.cancellation_reason}
      </p>
      <p className="m-0 mt-1 text-[11px] text-muted-foreground dark:text-[#4a5070]">
        Storniert von {who}
        {report.cancelled_at
          ? ` am ${formatDateTime(report.cancelled_at)}`
          : ""}
        . Der Rapport bleibt als Nachweis erhalten, zählt aber nicht mehr
        in Arbeitszeit- und Auswertungssummen.
      </p>
    </div>
  );
}
